import React, { useState, useEffect } from 'react';
import { cuentasService } from '../../services/cuentas';
import toast from 'react-hot-toast';
import { TrashIcon, EyeIcon, XMarkIcon, UserIcon, CalendarIcon, UsersIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

// =======================
// MODAL DE DETALLE CLÍNICA
// =======================
const AdminClinicDetailModal = ({ isOpen, onClose, clinicId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && clinicId) {
            fetchDetail();
        }
    }, [isOpen, clinicId]);

    const fetchDetail = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await cuentasService.getCuentaDetail(clinicId);
            setData(response);
        } catch (err) {
            console.error('Error fetching clinic detail:', err);
            setError('Error al cargar los detalles de la clínica');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#2A9D8F] to-[#227f74] px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <BuildingOfficeIcon className="w-6 h-6" />
                                {loading ? 'Cargando...' : data?.nombre || 'Detalle Clínica'}
                            </h3>
                            {data?.plan && (
                                <span className="inline-flex mt-1 px-2 py-0.5 text-xs font-medium bg-white/20 text-white rounded-full">
                                    {data.plan.nombre}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors p-1"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2A9D8F]"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500">{error}</div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* KPIs Grid */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-blue-50 rounded-xl p-4 text-center">
                                    <UsersIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-blue-700">{data.stats?.total_pacientes || 0}</p>
                                    <p className="text-xs text-blue-600">Pacientes</p>
                                </div>
                                <div className="bg-green-50 rounded-xl p-4 text-center">
                                    <CalendarIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-green-700">{data.stats?.total_citas || 0}</p>
                                    <p className="text-xs text-green-600">Citas Totales</p>
                                </div>
                                <div className="bg-purple-50 rounded-xl p-4 text-center">
                                    <UserIcon className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-purple-700">{data.usuarios?.length || 0}</p>
                                    <p className="text-xs text-purple-600">Usuarios</p>
                                </div>
                            </div>

                            {/* Owner Card */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <UserIcon className="w-4 h-4" /> Propietario
                                </h4>
                                {data.propietario ? (
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-[#2A9D8F] rounded-full flex items-center justify-center text-white font-bold text-lg">
                                            {data.propietario.nombre?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">{data.propietario.nombre}</p>
                                            <p className="text-sm text-gray-500">{data.propietario.email}</p>
                                            {data.propietario.telefono && (
                                                <p className="text-sm text-gray-500">{data.propietario.telefono}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">Sin propietario asignado</p>
                                )}
                            </div>

                            {/* Team Table */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <UsersIcon className="w-4 h-4" /> Equipo ({data.usuarios?.length || 0})
                                </h4>
                                {data.usuarios && data.usuarios.length > 0 ? (
                                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Último Acceso</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {data.usuarios.map((user) => (
                                                    <tr key={user.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.nombre}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                                user.rol === 'admin' 
                                                                    ? 'bg-amber-100 text-amber-800' 
                                                                    : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                                {user.rol}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                            {user.last_login 
                                                                ? new Date(user.last_login).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
                                                                : 'Nunca'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">Sin usuarios registrados</p>
                                )}
                            </div>

                            {/* Plan Details */}
                            {data.plan && (
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Detalles del Plan</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Precio</p>
                                            <p className="font-semibold">${data.plan.precio_mensual?.toLocaleString('es-CL')}/mes</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Máx. Usuarios</p>
                                            <p className="font-semibold">{data.plan.max_usuarios}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">WhatsApp</p>
                                            <p className="font-semibold">{data.plan.limite_whatsapp} msgs</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Estado Suscripción</p>
                                            <p className="font-semibold capitalize">{data.subscription_status}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};


// =======================
// PÁGINA PRINCIPAL
// =======================
const GestionClinicasPage = () => {
    const [cuentas, setCuentas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedClinicId, setSelectedClinicId] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    useEffect(() => {
        cargarCuentas();
    }, []);

    const cargarCuentas = async () => {
        try {
            const data = await cuentasService.getAllCuentas();
            setCuentas(data);
        } catch (error) {
            console.error('Error cargando cuentas:', error);
            toast.error('Error al cargar las clínicas');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEstado = async (cuenta) => {
        const nuevoEstado = cuenta.subscription_status === 'active' ? 'inactive' : 'active';
        try {
            await cuentasService.updateCuenta(cuenta.id, { subscription_status: nuevoEstado });
            setCuentas(cuentas.map(c => c.id === cuenta.id ? { ...c, subscription_status: nuevoEstado } : c));
            toast.success(`Clínica ${nuevoEstado === 'active' ? 'activada' : 'desactivada'} correctamente`);
        } catch (error) {
            console.error('Error actualizando estado:', error);
            toast.error('Error al actualizar el estado');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta clínica? Esta acción no se puede deshacer y borrará todos los datos asociados.')) {
            return;
        }

        try {
            await cuentasService.deleteCuenta(id);
            setCuentas(cuentas.filter(c => c.id !== id));
            toast.success('Clínica eliminada correctamente');
        } catch (error) {
            console.error('Error eliminando clínica:', error);
            toast.error('Error al eliminar la clínica');
        }
    };

    const handleViewDetail = (id) => {
        setSelectedClinicId(id);
        setShowDetailModal(true);
    };

    if (loading) {
        return <div className="p-4 text-center">Cargando clínicas...</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-6 text-gray-800">Gestión de Clínicas (SaaS)</h1>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre Clínica</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuarios</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Registro</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {cuentas.map((cuenta) => (
                                <tr key={cuenta.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {cuenta.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {cuenta.nombre}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                        {cuenta.plan_nombre || cuenta.plan}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {cuenta.total_usuarios}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(cuenta.creado_en).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            cuenta.subscription_status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : cuenta.subscription_status === 'trial'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : cuenta.subscription_status === 'past_due'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : cuenta.subscription_status === 'canceled'
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {cuenta.subscription_status === 'active' ? 'Activo' :
                                             cuenta.subscription_status === 'trial' ? 'Prueba' :
                                             cuenta.subscription_status === 'past_due' ? 'Pago Pendiente' :
                                             cuenta.subscription_status === 'canceled' ? 'Cancelado' :
                                             cuenta.subscription_status === 'inactive' ? 'Inactivo' :
                                             cuenta.subscription_status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2 flex items-center">
                                        <button
                                            onClick={() => handleViewDetail(cuenta.id)}
                                            className="text-[#2A9D8F] hover:text-[#227f74] p-1"
                                            title="Ver detalle"
                                        >
                                            <EyeIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleToggleEstado(cuenta)}
                                            className={`text-xs px-3 py-1 rounded border ${cuenta.subscription_status === 'active'
                                                    ? 'border-red-300 text-red-600 hover:bg-red-50'
                                                    : 'border-green-300 text-green-600 hover:bg-green-50'
                                                }`}
                                        >
                                            {cuenta.subscription_status === 'active' ? 'Desactivar' : 'Activar'}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(cuenta.id)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Eliminar clínica"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Detalle */}
            <AdminClinicDetailModal
                isOpen={showDetailModal}
                onClose={() => setShowDetailModal(false)}
                clinicId={selectedClinicId}
            />
        </div>
    );
};

export default GestionClinicasPage;
